import { useQuery } from "@tanstack/react-query"
import { api } from "@/api"

export const PERSONALIZED_SUMMARY_QUERY_KEY = "personalized-summary"

export interface PersonalizedSummary {
    id: string
    summary: string
    billId: string
    billTitle: string
    profileId: string
    profileName: string
    createdAt: string
}

interface UsePersonalizedSummaryParams {
    billId?: string
    profileId?: string
    boardId?: string
    nextBillId?: string
    enabled: boolean
}

// API function to fetch existing personalized summary
const fetchPersonalizedSummary = async (
    billId: string,
    boardId?: string,
    profileId?: string,
    nextBillId?: string,
): Promise<PersonalizedSummary | null> => {
    const params = new URLSearchParams()
    if (nextBillId) {
        params.set("next_bill", nextBillId)
    }

    if (boardId) {
        params.append("ai_tracking_board_id", boardId)
    }

    if (profileId) {
        params.append("personalization_profile_id", profileId)
    }

    const response = await api.get(`/api/personalization/bill-summaries/${billId}?${params.toString()}`)

    // Handle error responses (404 for missing profiles, etc.)
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 404 && errorData.error) {
            // Throw error with message for component to handle
            throw new Error(errorData.error)
        }
        // For other errors, just return null (no summary available)
        return null
    }

    const data = await response.json()

    // Handle board-specific responses where board has no profile configured
    if (boardId && data.board_has_profile === false) {
        // For boards without profiles, return null to indicate no summary available
        // The component will handle this case appropriately
        return null
    }

    if (!data.summary) {
        return null
    }

    return {
        id: data.summary.id,
        summary: data.summary.content,
        billId: data.summary.bill_id,
        billTitle: data.summary.bill_title,
        profileId: data.summary.profile_id,
        profileName: data.summary.profile_name,
        createdAt: data.summary.created_at,
    }
}

export const usePersonalizedSummary = ({
    billId,
    boardId,
    profileId,
    nextBillId,
    enabled,
}: UsePersonalizedSummaryParams) => {
    return useQuery({
        queryKey: [PERSONALIZED_SUMMARY_QUERY_KEY, billId, boardId, profileId, nextBillId],
        queryFn: () => fetchPersonalizedSummary(billId, boardId, profileId, nextBillId),
        staleTime: 5 * 60 * 1000,
        enabled,
        // Don't retry on profile configuration errors
        retry: (failureCount: number, error: unknown) => {
            if (
                error &&
                typeof error === "object" &&
                "message" in error &&
                typeof error.message === "string" &&
                error.message.includes("personalization profile configured")
            ) {
                return false
            }
            return failureCount < 3
        },
        // Error handling moved to component level to avoid deprecated onError
    })
}
